import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import type { FlowProgress } from '../flow/navigation'
import { uiStrings } from '../lib/strings'

/** A labelled way somewhere else, drawn by the chrome and decided by the route. */
export interface ChromeControl {
  readonly label: string
  readonly onSelect: () => void
}

/**
 * The way back to the screen she left when she came in here to change an
 * answer, offered once by the route and drawn by every screen's shell.
 *
 * A context rather than a prop because it belongs to none of the steps: a
 * question screen has no idea whether she arrived from the deck or from the
 * screen before it, and six step components each remembering to forward it is
 * six places to forget one — the same reason the counter and the remainder are
 * drawn here rather than by each step.
 */
const ReturnPathContext = createContext<ChromeControl | null>(null)

export function ReturnPathProvider({ value, children }: { readonly value: ChromeControl | null; readonly children: ReactNode }) {
  return <ReturnPathContext.Provider value={value}>{children}</ReturnPathContext.Provider>
}

/**
 * The frame the whole customer flow sits in: 390px-first, the boutique's name,
 * and nothing else. No cart, no counter badge, no header nav — the room is one
 * thing at a time.
 *
 * Progress is a row of SEGMENTS rather than a creeping bar, which is what
 * `ref/design/before-the-hour/Quiz.dc.html` draws. On a form this long the
 * difference is the whole point: a bar at 40% says "still going", and eleven
 * segments with four unfilled says "four left".
 *
 * It draws them only while she is still being ASKED. Past the questionnaire
 * every segment is filled, which is a progress bar saying "done" — decoration
 * charging 14px of the one screen that wants the room. What is left there is
 * the hairline the header has always fallen back to when a flow has no
 * questionnaire at all, so the header keeps its bottom edge either way.
 */
export function RoomHeader({
  displayName,
  title,
  progress,
  onBack,
  backLabel,
  action,
  advance,
  locale,
}: {
  readonly displayName: string
  /**
   * The screen's own heading, hoisted into the header by a step that would
   * rather spend the room on its content. It is still the page's `h1`; the
   * step that hands it over draws none of its own, so there is exactly one.
   */
  readonly title?: string | undefined
  readonly progress: FlowProgress
  readonly onBack?: (() => void) | undefined
  readonly backLabel: string
  /**
   * A control in the top right, for the step that asks for one. Drawn as a
   * mark rather than as a word — see the note beside it below.
   */
  readonly action?: (ChromeControl & { readonly icon?: 'pencil' | 'share' }) | undefined
  /**
   * The way on, when the screen under the header has no room for one.
   *
   * The deck is that screen: it is a gown from edge to edge with nothing
   * beneath it, so the button that leaves it lives up here. `ready` is the
   * screen saying she has enough to go on with — the same threshold the
   * server resolved from her tier — and it fills the button rather than
   * enabling it. It is pressable the whole time: this is a room, not a form,
   * and "you may stop now" is not the same as "you may not stop yet".
   */
  readonly advance?: (ChromeControl & { readonly ready?: boolean; readonly hint?: { readonly text: string; readonly onDismiss: () => void } }) | undefined
  readonly locale: string
}) {
  const t = uiStrings(locale)
  return (
    <header className="sticky top-0 z-40" style={{ background: 'color-mix(in srgb, var(--fr-ground-deep) 88%, transparent)', backdropFilter: 'blur(8px)' }}>
      <div className="mx-auto flex max-w-[520px] items-center gap-3 px-5 pt-4 pb-3">
        {title ? (
          /*
           * The boutique's name comes OFF this row when a title arrives. A
           * name, a heading and a control do not fit across 390px, and of the
           * three the name is the one she can least use: it is on the welcome
           * screen she came through, on the summary she is walking towards,
           * and — on the one screen made of nothing but this boutique's own
           * stock — the least in need of saying whose room she is in.
           *
           * Set smaller here than a step draws its own heading, because it is
           * sharing a 390px line with TWO controls now — the pencil and the
           * way on — and was already set smaller for sharing it with one: at the 1.75rem the deck
           * used, "Hang what you love" wants ~212px of the ~208 left beside
           * "Edit profile" and wraps to two, which would spend the room this
           * whole change is handing back. It still wraps rather than
           * truncates if a boutique writes a longer one — the header grows and
           * nothing is cut, which is the right way round.
           */
          <h1 className="fr-display min-w-0 flex-1 text-[1.25rem] leading-tight">{title}</h1>
        ) : (
          <>
            {onBack ? (
              <button type="button" className="fr-iconbtn shrink-0" onClick={onBack} aria-label={backLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <span className="w-9 shrink-0" aria-hidden="true" />
            )}
            <span className="fr-eyebrow flex-1 text-center">{displayName}</span>
            {/* Balances the arrow so the name sits centred, unless a real control does. */}
            {action ? null : <span className="w-9 shrink-0" aria-hidden="true" />}
          </>
        )}
        {/*
          A pencil, not "EDIT PROFILE".
          Three things share a 390px line here — a heading, this, and the way
          on — and of the three this is the one a mark can carry: everyone
          knows a pencil, and the word it replaces was 100px of the heading's
          width. The label it dropped is still its accessible name and its
          tooltip, so nothing that reads the screen aloud lost anything.
        */}
        {action ? (
          <button
            type="button"
            className="fr-iconbtn shrink-0"
            aria-label={action.label}
            title={action.label}
            onClick={action.onSelect}
            data-testid={action.icon === 'share' ? 'room-share-open' : undefined}
          >
            {action.icon === 'share' ? (
              // The share mark everyone knows from their phone: a tray with an arrow out of it.
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12" />
                <path d="M8 7l4-4 4 4" />
                <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17Z" />
                <path d="M14.5 6.5 17.5 9.5" />
              </svg>
            )}
          </button>
        ) : null}
        {advance ? (
          <div className="relative shrink-0">
            <button
              type="button"
              className="fr-btn-go"
              data-ready={advance.ready ? true : undefined}
              data-hinting={advance.hint ? true : undefined}
              onClick={advance.onSelect}
            >
              {advance.label}
            </button>
            {advance.hint ? (
              <button type="button" className="fr-go-hint" role="status" data-testid="advance-hint" onClick={advance.hint.onDismiss}>
                {advance.hint.text}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {progress.inQuestionnaire ? (
        <div
          className="mx-auto flex max-w-[520px] gap-1.5 px-5 pb-3"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.position}
          aria-valuetext={t.stepCounter(progress.position, progress.total)}
        >
          {Array.from({ length: progress.total }, (_, i) => (
            <span
              key={i}
              // The state is on the element, not only in its colour: a test
              // that reads a hard-coded hex is a test that breaks on a rebrand.
              data-filled={i < progress.position}
              className="h-[2px] flex-1 transition-colors duration-500 ease-out"
              style={{ background: i < progress.position ? 'var(--fr-primary)' : 'var(--fr-rule)' }}
            />
          ))}
        </div>
      ) : (
        <div className="h-px w-full" style={{ background: 'var(--fr-rule)' }} />
      )}
    </header>
  )
}

/**
 * One screen.
 *
 * It fills whatever the header leaves rather than subtracting a guessed header
 * height — `.fr-room` is the flex column that makes that work.
 *
 * Progress is rendered HERE rather than by each step: the counter above and the
 * "n more" line below the button are the same two lines on every screen of the
 * questionnaire, and four copies of them is four places to forget one.
 */
export function StepShell({
  title,
  body,
  children,
  footer,
  progress,
  locale,
  skip,
  wide = false,
}: {
  readonly title?: string
  readonly body?: string
  readonly children: ReactNode
  readonly footer?: ReactNode
  /** Where she is. Renders the counter and the remainder when in the questionnaire. */
  readonly progress?: FlowProgress
  readonly locale?: string
  /** Shown at the TOP, where she can see it without scrolling the form. */
  readonly skip?: { readonly label: string; readonly onSkip: () => void; readonly disabled?: boolean }
  readonly wide?: boolean
}) {
  const t = uiStrings(locale ?? 'en')
  const returnPath = useContext(ReturnPathContext)
  const counting = progress?.inQuestionnaire === true
  const chrome = counting || Boolean(skip)
  return (
    <div className={`mx-auto flex w-full flex-1 flex-col px-5 pb-8 ${wide ? 'max-w-[720px]' : 'max-w-[520px]'}`}>
      {chrome ? (
        <div className="flex items-center justify-between gap-3 pt-4">
          <span className="fr-eyebrow">{counting && progress ? t.stepCounter(progress.position, progress.total) : ''}</span>
          {skip ? (
            <button type="button" className="fr-btn-quiet fr-btn-target" disabled={skip.disabled} onClick={skip.onSkip}>
              {skip.label}
            </button>
          ) : null}
        </div>
      ) : null}
      {(title || body) && (
        <div className={chrome ? 'pt-5 pb-7' : 'pt-9 pb-7'}>
          {title ? <h1 className="fr-display text-[2rem] leading-[1.08]">{title}</h1> : null}
          {body ? (
            <p className="mt-3 max-w-[38ch] text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {body}
            </p>
          ) : null}
        </div>
      )}
      <div className="flex-1">{children}</div>
      {footer || counting ? (
        <div
          className="sticky bottom-0 pt-10"
          /*
            The bar paints its own ground, or it slices the form it floats
            over. It is sticky and the questionnaire scrolls beneath it, so
            anything transparent here is a window onto the middle of a field:
            on a 375px screen the measurements step loads with "WAIST - CM"
            cut in half by Continue, because the label sits at 717-733 and the
            button at 726-780. The two rows below used to paint themselves and
            the button's row did not, which fixed a third of the bar.

            A gradient rather than a flat fill, ending exactly where the top
            padding does, so content dissolves into the bar instead of meeting
            a hard edge. The fade is `pt-10` rather than the `pt-6` that was
            here because 24px is not far enough to read as a fade: a label
            caught in it still looks clipped. 40px is the distance the
            shortlist's own footer already fades over.
          */
          style={{ background: 'linear-gradient(to bottom, transparent, var(--fr-ground-deep) 2.5rem)' }}
        >
          {footer}
          {/*
            The line under the button says one of two things, never both.
            Walking the form forward it counts down to the end of it. Having
            come back in from the deck to change one answer it is the way out
            again — which is the honest thing to say in that mode, because
            "eight more, about two minutes" is a promise about a walk she is
            not taking.

            It sits here rather than beside the skip control at the top for
            three reasons. It replaces a line that is already on the screen
            instead of adding a row, so the normal walk through the
            questionnaire is not a pixel different. It is the same row as
            Continue, which is where the "I am done with this screen" actions
            live, and it is sticky, so it is under her thumb on every screen
            of the questionnaire without scrolling. And two underlined
            uppercase controls side by side would read as a pair of escape
            hatches — "Skip this" and "Back to browsing" mean quite different
            things — which in English at 390px would not fit beside the counter
            anyway.
          */}
          {counting && returnPath ? (
            <div className="pb-2 text-center">
              <button type="button" className="fr-btn-quiet fr-btn-target" onClick={returnPath.onSelect}>
                {returnPath.label}
              </button>
            </div>
          ) : counting && progress ? (
            <p
              className="pb-2 text-center text-[0.75rem]"
              style={{ color: 'var(--fr-ink-faint)' }}
            >
              {t.stepsRemaining(progress.remaining)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function RoomMessage({ title, detail, action }: { readonly title: string; readonly detail?: string; readonly action?: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-[520px] flex-col items-center justify-center gap-4 px-8 text-center">
      <h1 className="fr-display text-[1.75rem]">{title}</h1>
      {detail ? (
        <p className="text-sm leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
          {detail}
        </p>
      ) : null}
      {action}
    </div>
  )
}

export function RoomSpinner({ label }: { readonly label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <span className="fr-eyebrow animate-pulse">{label}</span>
    </div>
  )
}
